// ============================================================================
// PackDrop - one free pack per fortnight
// ============================================================================
// Locked -> swipe crates -> reel opening -> reveal.
//
// The reel is the whole point: prizes stream past a needle and decelerate
// onto the winner, with a near-miss planted just before it. That tension is
// what makes an opening worth watching rather than a number appearing.
//
// The SERVER decides the prize and credits the wallet before any of this
// animates. The reel is told where to stop - it never picks.
// ============================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import packDropService, {
  PackPrize, PackStatus, PACK_PRIZES_FALLBACK,
} from '../services/packDropService';
import './PackDrop.css';

const CELL = 96;
const CRATE_COUNT = 5;
// Cash ranks with Epic for celebration weight - real money is a big hit
// even when the number is small.
const RANK: Record<string, number> = { Common: 0, Uncommon: 1, Rare: 2, Epic: 3, Legendary: 4, Cash: 3, Jackpot: 4 };
// Display names for the internal tier keys (kept stable on the server).
const TIER_NAME: Record<string, string> = { Cash: 'PSS Bonus', Jackpot: 'Grand Prize' };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onWin?: (amount: number, kind: 'playcoins' | 'pkcash') => void;
}

// 'failed' is deliberately distinct from 'spent'. A status call that does not
// come back tells us nothing about whether the pack was opened, and saying
// "Pack opened" to someone who never opened one is the worst way to be wrong.
type Phase = 'loading' | 'failed' | 'locked' | 'ready' | 'opening' | 'reel' | 'reveal' | 'spent';

const PackDrop: React.FC<Props> = ({ isOpen, onClose, onWin }) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [status, setStatus] = useState<PackStatus | null>(null);
  const [prize, setPrize] = useState<PackPrize | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dim, setDim] = useState(false);
  const [shake, setShake] = useState<'' | 'on' | 'big'>('');

  const stripRef = useRef<HTMLDivElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const fxRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const later = (fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); };

  // ── Sound: synthesised, so there is nothing to license or ship ──────────
  const tone = useCallback((freq: number, dur: number, vol = 0.03, type: OscillatorType = 'square') => {
    try {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return;
      audioRef.current = audioRef.current || new Ctor();
      const ctx = audioRef.current;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = type; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(vol, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
      o.start(); o.stop(ctx.currentTime + dur);
    } catch { /* audio is a nicety, never a failure */ }
  }, []);

  // ── Particles on canvas (cheaper than DOM confetti on a phone) ──────────
  const burst = useCallback((color: string, power: number) => {
    const cv = fxRef.current;
    if (!cv) return;
    const r = cv.getBoundingClientRect();
    cv.width = r.width; cv.height = r.height;
    const g = cv.getContext('2d');
    if (!g) return;
    const cx = r.width / 2, cy = r.height * 0.44;
    const parts = Array.from({ length: 36 + Math.round(power * 34) }, () => {
      const a = Math.random() * Math.PI * 2;
      const sp = (1.5 + Math.random() * 5) * (1 + power * 0.5);
      return {
        x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 2,
        s: 2 + Math.random() * (3 + power * 3), life: 1, rot: Math.random() * 6,
        vr: (Math.random() - 0.5) * 0.3,
        c: Math.random() < 0.55 ? color : (Math.random() < 0.5 ? '#ffffff' : '#FFD700'),
      };
    });
    const step = () => {
      g.clearRect(0, 0, r.width, r.height);
      let alive = false;
      for (const p of parts) {
        if (p.life <= 0) continue;
        alive = true;
        p.vy += 0.14; p.vx *= 0.99; p.x += p.vx; p.y += p.vy; p.rot += p.vr; p.life -= 0.011;
        g.save(); g.globalAlpha = Math.max(0, p.life);
        g.translate(p.x, p.y); g.rotate(p.rot);
        g.fillStyle = p.c; g.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 1.6); g.restore();
      }
      if (alive) requestAnimationFrame(step); else g.clearRect(0, 0, r.width, r.height);
    };
    step();
  }, []);

  // ── Load status when opened ─────────────────────────────────────────────
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    setPhase('loading'); setError(null); setPrize(null); setDim(false);
    const s = await packDropService.getPackStatus();
    if (cancelledRef.current) return;
    setStatus(s);
    // Null means the call never landed - offline, cold start, or the
    // callable is not deployed. That is a failure to ASK, not an answer.
    if (!s) { setPhase('failed'); return; }
    if (!s.enabled) { setPhase('spent'); return; }
    if (s.opened) { setPrize(s.prize); setPhase('spent'); return; }
    setPhase(s.playedThisCycle ? 'ready' : 'locked');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    cancelledRef.current = false;
    load();
    return () => { cancelledRef.current = true; clearTimers(); };
  }, [isOpen, load]);

  useEffect(() => () => clearTimers(), []);

  // ── The reel ────────────────────────────────────────────────────────────
  const runReel = useCallback((won: PackPrize) => {
    setPhase('reel');
    const pool = status?.prizes?.length ? status.prizes : PACK_PRIZES_FALLBACK;
    const N = 68, WIN = N - 6;
    const cells: PackPrize[] = [];
    for (let i = 0; i < N; i++) {
      if (i === WIN) { cells.push(won); continue; }
      // A high tier planted just before the winner reads as a near-miss.
      if (i === WIN - 1) { cells.push(pool[pool.length - 1]); continue; }
      cells.push(pool[Math.floor(Math.random() * pool.length)]);
    }
    setReelCells(cells);

    // Animate on the next frame, once the strip has rendered.
    requestAnimationFrame(() => {
      const strip = stripRef.current, mask = maskRef.current;
      if (!strip || !mask) { finish(won); return; }
      const centre = mask.clientWidth / 2 - CELL / 2;
      const target = WIN * CELL - centre + (Math.random() * 40 - 20);
      const DUR = 5000;
      const t0 = performance.now();
      let lastCell = -1;
      const frame = (now: number) => {
        const t = Math.min(1, (now - t0) / DUR);
        const e = 1 - Math.pow(1 - t, 5);           // long readable crawl at the end
        const x = target * e;
        strip.style.transform = `translateX(${-x}px)`;
        strip.style.filter = t < 0.55 ? `blur(${(1 - t / 0.55) * 3}px)` : 'none';
        const cell = Math.floor(x / CELL);
        if (cell !== lastCell) { lastCell = cell; if (t < 0.99) tone(560 + t * 420, 0.028, 0.026); }
        if (t < 1) requestAnimationFrame(frame); else finish(won);
      };
      requestAnimationFrame(frame);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tone]);

  const [reelCells, setReelCells] = useState<PackPrize[]>([]);

  const finish = useCallback((won: PackPrize) => {
    const power = (RANK[won.tier] ?? 0) / 4;
    setShake(power >= 0.75 ? 'big' : 'on');
    burst(won.color, power);
    tone(660, 0.12, 0.05, 'sine');
    later(() => tone(880, 0.14, 0.05, 'sine'), 130);
    if (power >= 0.75) later(() => tone(1320, 0.2, 0.045, 'sine'), 260);
    later(() => setShake(''), 800);
    later(() => { setPhase('reveal'); onWin?.(won.value, won.kind === 'pkcash' ? 'pkcash' : 'playcoins'); }, 360);
  }, [burst, tone, onWin]);

  // ── Crate carousel ──────────────────────────────────────────────────────
  // Native scroll-snap gave no sense of travel: crates arrived rather than
  // moved. This drives position itself, so a flick carries momentum, decays,
  // and settles - and every crate is transformed by its distance from centre,
  // which is what makes the movement readable. Position is held in refs and
  // written straight to the DOM each frame; putting it in state would mean a
  // React render per frame for something no other component cares about.
  const STEP = 152;                        // crate width + gap, in px
  const pos = useRef(2);                   // continuous index, 0..CRATES-1
  const vel = useRef(0);                   // index units per frame
  const target = useRef(2);
  const rafRef = useRef<number | null>(null);
  const dragging = useRef(false);
  const dragFrom = useRef({ x: 0, pos: 2, moved: 0, lastX: 0, lastT: 0 });

  const paint = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const kids = track.children;
    for (let i = 0; i < kids.length; i++) {
      const el = kids[i] as HTMLElement;
      const d = i - pos.current;                       // signed distance
      const ad = Math.min(Math.abs(d), 2.4);
      // Depth and turn scale with distance, so the row reads as a carousel
      // rather than a filmstrip - you can see a crate swing away as it goes.
      el.style.transform =
        `translate3d(${d * STEP}px, ${ad * 6}px, ${-ad * 80}px)` +
        ` rotateY(${Math.max(-38, Math.min(38, -d * 17))}deg)` +
        ` scale(${Math.max(0.6, 1 - ad * 0.17)})`;
      el.style.opacity = String(Math.max(0.16, 1 - ad * 0.36));
      el.style.zIndex = String(100 - Math.round(ad * 10));
    }
  }, []);

  const stepFrame = useCallback(() => {
    const MAX = CRATE_COUNT - 1;
    if (!dragging.current) {
      if (Math.abs(vel.current) > 0.0016) {
        pos.current += vel.current;
        vel.current *= 0.935;                          // friction
        // Past the ends the drift is halved and pulled back, so a hard flick
        // stops against a soft wall instead of flying off.
        if (pos.current < 0 || pos.current > MAX) vel.current *= 0.55;
        target.current = Math.max(0, Math.min(MAX, Math.round(pos.current)));
      } else {
        const diff = target.current - pos.current;
        if (Math.abs(diff) > 0.0008) pos.current += diff * 0.15;
        else { pos.current = target.current; vel.current = 0; }
      }
    }
    paint();
    rafRef.current = requestAnimationFrame(stepFrame);
  }, [paint]);

  useEffect(() => {
    if (phase !== 'ready' && phase !== 'opening') return;
    pos.current = 2; target.current = 2; vel.current = 0;
    rafRef.current = requestAnimationFrame(stepFrame);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [phase, stepFrame]);

  const onDown = (e: React.PointerEvent) => {
    dragging.current = true;
    vel.current = 0;
    dragFrom.current = {
      x: e.clientX, pos: pos.current, moved: 0,
      lastX: e.clientX, lastT: performance.now(),
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const f = dragFrom.current;
    const dx = e.clientX - f.x;
    f.moved = Math.max(f.moved, Math.abs(dx));

    let next = f.pos - dx / STEP;
    const MAX = CRATE_COUNT - 1;
    // Rubber band: the ends still give, they just resist.
    if (next < 0) next *= 0.34;
    else if (next > MAX) next = MAX + (next - MAX) * 0.34;
    pos.current = next;

    const now = performance.now();
    const dt = Math.max(8, now - f.lastT);
    vel.current = -((e.clientX - f.lastX) / STEP) * (16 / dt);
    f.lastX = e.clientX; f.lastT = now;
  };

  const onUp = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    dragging.current = false;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    vel.current = Math.max(-0.42, Math.min(0.42, vel.current));
    target.current = Math.max(0, Math.min(CRATE_COUNT - 1,
      Math.round(pos.current + vel.current * 5)));
  };

  const onWheel = (e: React.WheelEvent) => {
    const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
    vel.current = 0;
    target.current = Math.max(0, Math.min(CRATE_COUNT - 1,
      Math.round(pos.current) + (delta > 0 ? 1 : -1)));
  };

  /** Tapping the centred crate opens it; tapping a neighbour glides to it. */
  const onCrateTap = (i: number) => {
    if (dragFrom.current.moved > 6) { dragFrom.current.moved = 0; return; }
    if (Math.abs(i - pos.current) > 0.45) { vel.current = 0; target.current = i; return; }
    handleOpen();
  };

  const handleOpen = useCallback(async () => {
    if (phase !== 'ready') return;
    setPhase('opening'); setDim(true); setError(null);

    later(() => tone(90, 0.08), 350);
    later(() => tone(110, 0.08), 700);
    later(() => tone(130, 0.08), 980);

    const r = await packDropService.openPack();
    if (!r.success || !r.prize) {
      setError(r.error || 'Could not open your pack');
      setPhase('ready'); setDim(false);
      return;
    }
    setPrize(r.prize);
    if (r.alreadyOpened) { setPhase('spent'); setDim(false); return; }
    later(() => runReel(r.prize as PackPrize), 900);
  }, [phase, runReel, tone]);

  if (!isOpen) return null;

  const endsIn = packDropService.formatUntil(status?.endsAt);
  const tierColor = prize?.color || '#00D9D9';

  return (
    <div className="pd-overlay" onClick={onClose} role="dialog" aria-label="Pack Drop">
      <div className={`pd-modal ${shake ? `pd-shake ${shake === 'big' ? 'pd-shake-big' : ''}` : ''}`}
        onClick={e => e.stopPropagation()}>
        <button className="pd-close" onClick={onClose} aria-label="Close">×</button>

        <div className={`pd-vig ${dim ? 'on' : ''}`} />
        {prize && phase === 'reveal' && (RANK[prize.tier] ?? 0) >= 2 && (
          <div className="pd-rays on" style={{ ['--ray' as any]: tierColor }} />
        )}
        <canvas className="pd-fx" ref={fxRef} />

        <div className="pd-head">
          <h2>Pack Drop</h2>
          <div className="pd-sub">
            {phase === 'locked' && 'Play a hand to unlock'}
            {phase === 'ready' && 'Pick a crate'}
            {phase === 'opening' && 'Opening…'}
            {phase === 'reel' && 'Rolling…'}
            {phase === 'reveal' && 'Added to your balance'}
            {phase === 'spent' && endsIn && <>Next pack in <b className="pd-timer">{endsIn}</b></>}
          </div>
        </div>

        <div className="pd-stage">
          {phase === 'loading' && <div className="pd-muted">Loading…</div>}

          {phase === 'failed' && (
            <div className="pd-lock">
              <div className="pd-ring">!</div>
              <h3>Couldn't reach the drop</h3>
              <p>Your pack is untouched — we just couldn't check on it. Try again
                in a moment.</p>
            </div>
          )}

          {phase === 'locked' && (
            <div className="pd-lock">
              <div className="pd-ring">◈</div>
              <h3>Your pack is waiting</h3>
              <p>Play one hand this cycle to unlock it. Packs don't stack — this one
                expires when the next drops{endsIn ? `, in ${endsIn}` : ''}.</p>
            </div>
          )}

          {(phase === 'ready' || phase === 'opening') && (
            <>
              <div className="pd-hint">Drag to spin · tap to open</div>
              <div className="pd-stagewrap"
                onPointerDown={onDown} onPointerMove={onMove}
                onPointerUp={onUp} onPointerCancel={onUp}
                onWheel={onWheel}>
                <div className="pd-track" ref={trackRef}>
                  {Array.from({ length: CRATE_COUNT }, (_, k) => k + 1).map((i, idx) => (
                    <button key={i} className="pd-crate" onClick={() => onCrateTap(idx)}
                      disabled={phase === 'opening'} aria-label={`Open crate ${i}`}>
                      <span className="pd-seal">PSS</span>
                      <span className="pd-idx">Crate {i}</span>
                    </button>
                  ))}
                </div>
              </div>
              {error && <div className="pd-err">{error}</div>}
            </>
          )}

          {phase === 'reel' && (
            <div className="pd-reelbox">
              <div className="pd-reelmask" ref={maskRef}>
                <div className="pd-strip" ref={stripRef}>
                  {reelCells.map((p, i) => (
                    <div className="pd-cell" key={i}>
                      <div className="pd-chip" style={{ background: `linear-gradient(160deg, ${p.color}, ${p.color}99)` }}>●</div>
                      <div className="pd-amt" style={{ color: p.color }}>{p.label}</div>
                      <div className="pd-cur">{p.kind === 'pkcash' ? 'PSS COINS' : 'PC'}</div>
                    </div>
                  ))}
                </div>
                <div className="pd-needle" />
              </div>
            </div>
          )}

          {phase === 'reveal' && prize && (
            <div className="pd-reveal">
              <span className="pd-tier" style={{
                color: prize.color, background: `${prize.color}1f`, border: `1px solid ${prize.color}55`,
              }}>{TIER_NAME[prize.tier] || prize.tier}</span>
              <p className="pd-prize" style={{ color: prize.color, textShadow: `0 0 40px ${prize.color}88` }}>
                {prize.label}
              </p>
              <p className="pd-unit">
                {prize.kind === 'pkcash' ? 'PSS Coins · 1x playthrough to redeem' : 'PlayCoins'}
              </p>
            </div>
          )}

          {phase === 'spent' && (
            <div className="pd-lock">
              <div className="pd-ring pd-ring-done">✓</div>
              <h3>{status?.enabled === false ? 'Pack Drop is paused' : 'Pack opened'}</h3>
              <p>
                {status?.enabled === false
                  ? 'Packs are switched off right now. Check back soon.'
                  : <>That's your pack for this cycle{prize
                      ? ` — ${prize.label} ${prize.kind === 'pkcash' ? 'PSS Coins' : 'PlayCoins'}`
                      : ''}.
                     The next one unlocks{endsIn ? ` in ${endsIn}` : ' soon'}.</>}
              </p>
            </div>
          )}
        </div>

        <div className="pd-foot">
          {phase === 'failed' && <button className="pd-btn" onClick={load}>Try again</button>}
          {phase === 'ready' && <button className="pd-btn" onClick={handleOpen}>Open pack</button>}
          {phase === 'locked' && <button className="pd-btn pd-ghost" onClick={onClose}>Go play a hand</button>}
          {(phase === 'reveal' || phase === 'spent') && (
            <button className="pd-btn" onClick={onClose}>
              {phase === 'reveal' ? 'Collect' : 'Close'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackDrop;
